import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ZefForm, ZefFormsManagerService } from '@zerops/zef/forms';
import { AppState } from '@zerops/zerops/app';
import {
  FormState,
  validate
} from 'ngrx-forms';
import { pattern } from 'ngrx-forms/validation';
import { FEATURE_NAME } from './l7-config-dialog.constant';

export interface L7ConfigFormValue {
  // Connection Handling
  worker_connections: string;
  multi_accept: boolean | 'dflt';
  keepalive_timeout: string;
  keepalive_requests: string;
  proxy_read_timeout: string;
  proxy_send_timeout: string;

  // Client Request Settings
  client_header_timeout: string;
  client_body_timeout: string;
  client_max_body_size: string;
  reset_timedout_connection: boolean | 'dflt';
  send_timeout: string;

  // Buffer Settings
  client_header_buffer_size: string;
  large_client_header_buffers_number: string;
  large_client_header_buffers_size: string;
  client_body_buffer_size: string;

  // Proxy Settings
  proxy_request_buffering: boolean | 'dflt';
  proxy_buffering: boolean | 'dflt';
  proxy_buffer_size: string;
  proxy_buffers_number: string;
  proxy_buffers_size: string;
  proxy_busy_buffers_size: string;
  proxy_max_temp_file_size: string;

  // Performance Optimization
  sendfile: boolean | 'dflt';
  tcp_nopush: boolean | 'dflt';
  tcp_nodelay: boolean | 'dflt';
  gzip: boolean | 'dflt';
  limit_rate: string;

  // File Cache Settings
  open_file_cache: boolean | 'dflt';
  open_file_cache_max: string;
  open_file_cache_inactive: string;
  open_file_cache_valid: string;
  open_file_cache_min_uses: string;
  open_file_cache_errors: boolean | 'dflt';

  // Security
  server_tokens: boolean | 'dflt';
}

export type L7ConfigFormState = FormState<L7ConfigFormValue>;

// Validation patterns for L7 HTTP balancer configuration
const timeIntervalPattern = /^(\d+(\.\d+)?([smhd]|ms)?)?$/i;
const sizePattern = /^(\d+(\.\d+)?[kmgtKMGT]?[bB]?)?$/i;
const numberPattern = /^\d+$/;

@Injectable({ providedIn: 'root' })
export class L7ConfigForm extends ZefForm<L7ConfigFormValue> {
  constructor(
    public store: Store<AppState>,
    public formsManager: ZefFormsManagerService
  ) {
    super(
      store,
      formsManager,
      FEATURE_NAME,
      {
        // Connection Handling - defaults as per specification
        worker_connections: '',
        multi_accept: 'dflt',
        keepalive_timeout: '',
        keepalive_requests: '',
        proxy_read_timeout: '',
        proxy_send_timeout: '',

        // Client Request Settings
        client_header_timeout: '',
        client_body_timeout: '',
        client_max_body_size: '',
        reset_timedout_connection: 'dflt',
        send_timeout: '',

        // Buffer Settings
        client_header_buffer_size: '',
        large_client_header_buffers_number: '',
        large_client_header_buffers_size: '',
        client_body_buffer_size: '',

        // Proxy Settings
        proxy_request_buffering: 'dflt',
        proxy_buffering: 'dflt',
        proxy_buffer_size: '',
        proxy_buffers_number: '',
        proxy_buffers_size: '',
        proxy_busy_buffers_size: '',
        proxy_max_temp_file_size: '',

        // Performance Optimization
        sendfile: 'dflt',
        tcp_nopush: 'dflt',
        tcp_nodelay: 'dflt',
        gzip: 'dflt',
        limit_rate: '',

        // File Cache Settings
        open_file_cache: 'dflt',
        open_file_cache_max: '',
        open_file_cache_inactive: '',
        open_file_cache_valid: '',
        open_file_cache_min_uses: '',
        open_file_cache_errors: 'dflt',

        // Security
        server_tokens: 'dflt'
      },
      {
        // Connection Handling validations
        worker_connections: validate(pattern(numberPattern)),
        // multi_accept: boolean, no validation needed
        keepalive_timeout: validate(pattern(timeIntervalPattern)),
        keepalive_requests: validate(pattern(numberPattern)),
        proxy_read_timeout: validate(pattern(timeIntervalPattern)),
        proxy_send_timeout: validate(pattern(timeIntervalPattern)),

        // Client Request Settings validations
        client_header_timeout: validate(pattern(timeIntervalPattern)),
        client_body_timeout: validate(pattern(timeIntervalPattern)),
        client_max_body_size: validate(pattern(sizePattern)),
        // reset_timedout_connection: boolean, no validation needed
        send_timeout: validate(pattern(timeIntervalPattern)),

        // Buffer Settings validations
        client_header_buffer_size: validate(pattern(sizePattern)),
        large_client_header_buffers_number: validate(pattern(numberPattern)),
        large_client_header_buffers_size: validate(pattern(sizePattern)),
        client_body_buffer_size: validate(pattern(sizePattern)),

        // Proxy Settings validations
        // proxy_request_buffering: boolean, no validation needed
        // proxy_buffering: boolean, no validation needed
        proxy_buffer_size: validate(pattern(sizePattern)),
        proxy_buffers_number: validate(pattern(numberPattern)),
        proxy_buffers_size: validate(pattern(sizePattern)),
        proxy_busy_buffers_size: validate(pattern(sizePattern)),
        proxy_max_temp_file_size: validate(pattern(sizePattern)),

        // Performance Optimization validations
        // sendfile: boolean, no validation needed
        // tcp_nopush: boolean, no validation needed
        // tcp_nodelay: boolean, no validation needed
        // gzip: boolean, no validation needed
        limit_rate: validate(pattern(/^(\d+(\.\d+)?[kmgtKMGT]?[bB]?|\d+)$/i)), // Size or number

        // File Cache Settings validations
        // open_file_cache: boolean, no validation needed
        open_file_cache_max: validate(pattern(numberPattern)),
        open_file_cache_inactive: validate(pattern(timeIntervalPattern)),
        open_file_cache_valid: validate(pattern(timeIntervalPattern)),
        open_file_cache_min_uses: validate(pattern(numberPattern)),
        // open_file_cache_errors: boolean, no validation needed

        // Security validations
        // server_tokens: boolean, no validation needed
      }
    );
  }
}
